import * as React from 'react';
import { useState } from 'react';
import {
	Box,
	TextField,
	MenuItem,
	Modal,
	Typography,
	IconButton,
	Card,
	CardContent,
	Table,
	TableBody,
	TableRow,
	TableCell,
	TableHead,
} from '@mui/material';
import { GridToolbar, GridColDef } from '@mui/x-data-grid';
import Grid from '@mui/material/Grid2';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
	StyledButton,
	StyledDataGrid,
	StyledTab,
	StyledTabs,
} from '../../component/ThemeComponent';
import { deleteOrder, getAllOrder } from '../../../services/api/OrderAPI';

interface OrderItem {
	productId: string;
	quantity: number;
	price: number;
	subtotal: number;
	weight: number;
	productName: string;
}

interface ShippingAddress {
	name: string;
	phone: string;
	address: string;
	city: string;
	state: string;
	country: string;
	postalCode: string;
	email: string;
}

interface Order {
	_id: string;
	orderId: string;
	orderStatus: string;
	paymentStatus: string;
	razorpayOrderId: string;
	shiprocketOrderId: string;
	shiprocketReferenceId: string;
	totalAmount: number;
	totalWeight: number;
	createdAt: string;
	updatedAt: string;
	userId: string;
	cancelReason: string | null;
	items: OrderItem[];
	shippingAddress: ShippingAddress;
}

const Orders: React.FC = () => {
	const [searchText, setSearchText] = useState<string>('');
	const [filterStatus, setFilterStatus] = useState<string>('all');
	const [open, setOpen] = useState<boolean>(false);
	const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
	const [tabValue, setTabValue] = useState<number>(0);
	const [rows, setRows] = useState<Order[]>([]);

	const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchText(event.target.value);
	};

	const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterStatus(event.target.value);
	};

	const handleViewDetails = (order: Order) => {
		setSelectedOrder(order);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
	};

	const fetchdataorder = async () => {
		const response = await getAllOrder();
		if (response) {
			console.log(response);
			const data = response.data.orders.map((order: Order) => ({
				...order,
				id: order._id, // Add id property
			}));
			setRows(data);
		} else {
			console.error('Failed to fetch orders');
		}
	};

	React.useEffect(() => {
		fetchdataorder();
	}, []);

	const filteredRows = rows.filter(
		(row) =>
			row.orderId.includes(searchText) ||
			row.shippingAddress.email.toLowerCase().includes(searchText.toLowerCase())
	);

	const columns: GridColDef[] = [
		{ field: 'orderId', headerName: 'Order ID', width: 150 },
		{ field: 'shippingAddress.email', headerName: 'Email', width: 200 },
		{ field: 'items', headerName: 'Ordered Items', width: 150 },
		{ field: 'createdAt', headerName: 'Date', width: 150 },
		{ field: 'orderStatus', headerName: 'Order Status', width: 150 },
		{ field: 'items.length', headerName: 'Item', width: 100 },
		{ field: 'totalAmount', headerName: 'Total Price', width: 150 },
		{ field: 'paymentStatus', headerName: 'Payment Status', width: 150 },
		{
			field: 'actions',
			headerName: 'Actions',
			width: 100,
			renderCell: (params) => (
				<IconButton onClick={() => handleViewDetails(params.row)}>
					<VisibilityIcon />
				</IconButton>
			),
		},
	];
	const handleDelete = async () => {
		if (selectedOrder) {
			try {
				const response = await deleteOrder(selectedOrder._id.toString());
				if (response.ok) {
					console.log(`Deleted order with ID: ${selectedOrder.orderId}`);
					// Close the modal after deletion
					setOpen(false);
					// Optionally, update the state to remove the deleted order from the list
				} else {
					console.error('Failed to delete the order');
				}
			} catch (error) {
				console.error('Error deleting the order:', error);
			}
		}
	};

	return (
		<Box sx={{ width: '100%' }}>
			<Typography variant="h3">Orders</Typography>
			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid size={{ xs: 12, sm: 6, md: 10 }}>
					<TextField
						label="Search"
						variant="outlined"
						value={searchText}
						onChange={handleSearch}
						fullWidth
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 2 }}>
					<TextField
						select
						label="Filter by Status"
						value={filterStatus}
						onChange={handleFilterChange}
						variant="outlined"
						fullWidth
					>
						<MenuItem value="all">All</MenuItem>
						<MenuItem value="new">New Order</MenuItem>
						<MenuItem value="shipped">Shipped</MenuItem>
						<MenuItem value="cancelled">Cancelled</MenuItem>
					</TextField>
				</Grid>
			</Grid>
			<Box sx={{ height: 600, width: 'auto' }}>
				<StyledDataGrid
					rows={filteredRows}
					columns={columns}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
					}}
					pageSizeOptions={[10]}
					slots={{ toolbar: GridToolbar }}
					getRowId={(row) => row._id} // Specify custom id
				/>
			</Box>
			<Modal
				open={open}
				onClose={handleClose}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
			>
				<Box
					sx={{
						position: 'absolute',
						top: '50%',
						left: '50%',
						transform: 'translate(-50%, -50%)',
						width: { xs: 300, sm: 400, md: '80%' },
						bgcolor: 'background.paper',
						border: '0px',
						boxShadow: 24,
						p: 4,
						borderRadius: '8px',
					}}
				>
					<Typography id="modal-modal-title" variant="h6" component="h2">
						Order Details
					</Typography>
					<Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
						<StyledTabs
							value={tabValue}
							onChange={handleTabChange}
							aria-label="order tabs"
						>
							<StyledTab label="Summary" />
							<StyledTab label="Item" />
							<StyledTab label="Customer" />
						</StyledTabs>
					</Box>
					{tabValue === 0 && selectedOrder && (
						<Card sx={{ borderRadius: '8px', mb: 2 }}>
							<CardContent>
								<Table>
									<TableBody>
										<TableRow>
											<TableCell>
												<strong>Order ID:</strong>
											</TableCell>
											<TableCell>{selectedOrder.orderId}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Order Status:</strong>
											</TableCell>
											<TableCell>{selectedOrder.orderStatus}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Email:</strong>
											</TableCell>
											<TableCell>
												{selectedOrder.shippingAddress.email}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Payment Status:</strong>
											</TableCell>
											<TableCell>{selectedOrder.paymentStatus}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Shipping Method:</strong>
											</TableCell>
											<TableCell>{selectedOrder.shiprocketOrderId}</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Total Price:</strong>
											</TableCell>
											<TableCell>{selectedOrder.totalAmount}</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					)}
					{tabValue === 1 && selectedOrder && (
						<Card sx={{ borderRadius: '8px', mb: 2 }}>
							<CardContent>
								<div>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>
													<strong>Ordered Item:</strong>
												</TableCell>
												<TableCell>
													<strong>Quantity:</strong>
												</TableCell>
												<TableCell>
													<strong>Product:</strong>
												</TableCell>
												<TableCell>
													<strong>Category:</strong>
												</TableCell>
												<TableCell>
													<strong>Price:</strong>
												</TableCell>
												<TableCell>
													<strong>Total:</strong>
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{selectedOrder.items.map((item) => (
												<TableRow key={item.productId}>
													<TableCell>{item.productName}</TableCell>
													<TableCell>{item.quantity}</TableCell>
													<TableCell>{item.productId}</TableCell>
													<TableCell>{item.weight}</TableCell>
													<TableCell>{item.price}</TableCell>
													<TableCell>{item.subtotal}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>
							</CardContent>
						</Card>
					)}
					{tabValue === 2 && selectedOrder && (
						<Card sx={{ borderRadius: '8px', mb: 2 }}>
							<CardContent>
								<Table>
									<TableBody>
										<TableRow>
											<TableCell>
												<strong>Customer Name:</strong>
											</TableCell>
											<TableCell>
												{selectedOrder.shippingAddress.name}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Email:</strong>
											</TableCell>
											<TableCell>
												{selectedOrder.shippingAddress.email}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Phone:</strong>
											</TableCell>
											<TableCell>
												{selectedOrder.shippingAddress.phone}
											</TableCell>
										</TableRow>
										<TableRow>
											<TableCell>
												<strong>Shipping Address:</strong>
											</TableCell>
											<TableCell>
												{selectedOrder.shippingAddress.address},{' '}
												{selectedOrder.shippingAddress.city},{' '}
												{selectedOrder.shippingAddress.state},{' '}
												{selectedOrder.shippingAddress.postalCode},{' '}
												{selectedOrder.shippingAddress.country}
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</CardContent>
						</Card>
					)}
					<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
						<StyledButton variant="contained" color="error" onClick={handleClose}>
							Cancel
						</StyledButton>
						<StyledButton variant="contained" color="primary" onClick={handleDelete}>
							Delete
						</StyledButton>
					</Box>
				</Box>
			</Modal>
		</Box>
	);
};

export default Orders;
